import { ReactNode } from "react";
import { Spinner, Stack, Text, XStack, YStack } from "tamagui";
import { OwnedPaymentMethod } from "@screen/Home/pages/Account/API/handlePaymentAPI";
import { kiosk } from "../kioskTheme";
import { PaymentSelection } from "../useKiosk";
import KioskIcon from "../KioskIcon";
import { TapPaySecurityNote } from "@components/payment/TapPayCompliance";

/** 卡別/付款方式左側徽章外框（統一尺寸，讓各列 icon 對齊）。 */
const LeadingBadge = ({ children, bg = "#fff" }: { children: ReactNode; bg?: string }) => (
  <Stack width={42} height={28} borderRadius={6} borderWidth={1} borderColor="#e6e9f0" backgroundColor={bg} ai="center" jc="center">
    {children}
  </Stack>
);

/** 依 bincode 首碼判斷卡別，回傳對應 logo 徽章（4=Visa、5/2=Mastercard、3=JCB，其餘泛用卡）。 */
const CardBrand = ({ bincode }: { bincode?: string }) => {
  const d = (bincode || "")[0];
  if (d === "4")
    return (
      <LeadingBadge>
        <Text fontSize={13} fontWeight="800" fontStyle="italic" color="#1a1f71" letterSpacing={-0.3}>
          VISA
        </Text>
      </LeadingBadge>
    );
  if (d === "5" || d === "2")
    return (
      <LeadingBadge>
        <XStack ai="center">
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#eb001b" />
          <Stack width={13} height={13} borderRadius={7} backgroundColor="#f79e1b" ml={-6} opacity={0.92} />
        </XStack>
      </LeadingBadge>
    );
  if (d === "3")
    return (
      <LeadingBadge>
        <Text fontSize={11} fontWeight="900" color="#0b6cb3" letterSpacing={-0.2}>
          JCB
        </Text>
      </LeadingBadge>
    );
  return (
    <LeadingBadge>
      <KioskIcon name="card" size={19} color="#8a93a6" />
    </LeadingBadge>
  );
};

/** 單列選項（左側徽章 + 標題/副標；選中以藍框表示，選中時右側顯示打勾）。 */
const OptionRow = ({ title, sub, selected, onPress, leading }: { title: string; sub?: string; selected: boolean; onPress: () => void; leading?: ReactNode }) => (
  <XStack
    ai="center"
    jc="space-between"
    px={14}
    py={13}
    gap={12}
    borderWidth={1.5}
    borderColor={selected ? kiosk.blue : kiosk.line}
    borderRadius={12}
    backgroundColor={selected ? "#f2f8ff" : "#fff"}
    onPress={onPress}
    pressStyle={{ opacity: 0.75 }}
    style={{ cursor: "pointer" }}>
    <XStack ai="center" gap={12} flex={1}>
      {leading}
      <YStack flex={1}>
        <Text fontSize={16} fontWeight="700" color={kiosk.ink}>
          {title}
        </Text>
        {sub ? (
          <Text fontSize={12.5} color={kiosk.grey} mt={2}>
            {sub}
          </Text>
        ) : null}
      </YStack>
    </XStack>
    {selected ? <KioskIcon name="check" size={19} color={kiosk.blue} strokeWidth={2.6} /> : null}
  </XStack>
);

/**
 * 付款方式選項清單（供 CheckoutGuide 的「怎麼付款？」步驟嵌入）。
 * 提供：已綁信用卡（卡別 logo）＋貨到付款/月結＋新增信用卡。
 * 只負責選擇，實際扣款由後端於送達後處理。
 */
const PaymentOptions = ({
  isMonthly,
  selected,
  onSelect,
  onAddCard,
  cards,
  loading,
  showApplePay,
}: {
  isMonthly: boolean;
  selected: PaymentSelection | null;
  onSelect: (v: PaymentSelection) => void;
  onAddCard: () => void;
  /** 已綁信用卡清單（由父層在結帳引導開啟時預先載入，進到本步驟時即有資料）。 */
  cards: OwnedPaymentMethod[];
  loading: boolean;
  /** 是否顯示 Apple Pay 選項（父層 CheckoutGuide 依 shouldOfferApplePayInLine 判斷後傳入）。 */
  showApplePay: boolean;
}) => {
  const cashType: PaymentSelection["type"] = isMonthly ? "monthly" : "cash_on_delivery";
  const cashTitle = isMonthly ? "月結" : "貨到付款";
  // 預選與否由父層決定（CheckoutGuide 於 onLoaded 時自動選第一張卡）；未選時 curType 為 undefined、不打勾。
  const curType = selected?.type;
  const curCardId = selected?.type === "credit_card" ? selected.paymentMethodId : "";
  const cardLabel = (c: OwnedPaymentMethod) => `信用卡 •••• ${c.card?.lastfour ?? "----"}`;

  return (
    <YStack gap={10} className="kiosk-stepin">
      {/* 信用卡在前（左側卡別 logo） */}
      {loading ? (
        <XStack ai="center" jc="center" py={16} gap={8}>
          <Spinner size="small" color={kiosk.blue} />
          <Text fontSize={13} color={kiosk.grey}>
            載入付款方式…
          </Text>
        </XStack>
      ) : (
        cards.map((c) => (
          <OptionRow
            key={c.payment_method_id}
            title={cardLabel(c)}
            sub={c.card?.issuer || c.nickname || "信用卡"}
            leading={<CardBrand bincode={c.card?.bincode} />}
            selected={curType === "credit_card" && curCardId === c.payment_method_id}
            onPress={() => onSelect({ type: "credit_card", paymentMethodId: c.payment_method_id, label: cardLabel(c) })}
          />
        ))
      )}

      {/* 貨到付款 / 月結（左側現金 icon） */}
      <OptionRow
        title={cashTitle}
        sub={isMonthly ? "月底結算" : "送達時付款"}
        leading={
          <LeadingBadge bg="#eafaf0">
            <KioskIcon name="cash" size={20} color={kiosk.green} />
          </LeadingBadge>
        }
        selected={curType === cashType}
        onPress={() => onSelect(isMonthly ? { type: "monthly" } : { type: "cash_on_delivery" })}
      />

      {/* Apple Pay（僅標記選取，不呼叫 TapPay；送達後依實際金額由後端走電子付款流程扣款） */}
      {showApplePay && (
        <OptionRow
          title="Apple Pay"
          sub="送達後依實際金額付款"
          leading={
            <LeadingBadge bg="#000">
              <Text color="#fff" fontWeight="700" fontSize={11}>
                 Pay
              </Text>
            </LeadingBadge>
          }
          selected={curType === "apple_pay"}
          onPress={() => onSelect({ type: "apple_pay" })}
        />
      )}

      {/* 新增信用卡（放最後） */}
      <XStack
        ai="center"
        jc="center"
        gap={6}
        px={14}
        py={14}
        borderWidth={1.5}
        borderColor={kiosk.line}
        borderStyle="dashed"
        borderRadius={12}
        onPress={onAddCard}
        pressStyle={{ opacity: 0.7 }}
        style={{ cursor: "pointer" }}>
        <Text fontSize={15.5} fontWeight="700" color={kiosk.blue}>
          ＋ 新增信用卡
        </Text>
      </XStack>

      {/* TapPay 金流安全揭露（審核要求：電子支付選項處需說明金流商與加密／PCI 認證） */}
      <TapPaySecurityNote compact />
    </YStack>
  );
};

export default PaymentOptions;
